// ============================================================================
// CabinetTypeListTab.tsx — вкладка кабинета со СПИСКОМ ВСТРОЕННОГО ТИПА
// (задачи/заявки) на существующих RPC платформы.
//
// Кабинет не дублирует списки: используется ТОТ ЖЕ AutoListForm, что в
// «Предприятии» (колонки из метаданных типа, карточки/список, поиск, отборы,
// группировки, папки, печать, офлайн-кэш). Запись открывается существующей
// AutoObjectForm поверх кабинета.
//
// Типов может быть несколько: показывается переключатель типов (TabBar).
// Никаких новых proto и серверных прав — данные ограничивает сервер
// (ListTasks/ListOrders: свои задачи/заявки по правам и полномочиям).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { friendlyError } from '@/lib/errors';
import { createLogger } from '@/lib/logger';
import { AutoListForm } from '@/components/autoforms/AutoListForm';
import { AutoObjectForm } from '@/components/autoforms/AutoObjectForm';
import { TabBar } from '@/components/ui/TabBar';
import { Button } from '@/components/ui/button';

const log = createLogger('CabinetTypeListTab');

/** Встроенные типы ядра, для которых есть общий клиентский список. */
export type CabinetListKind = 'task' | 'order';

interface TypeOption {
  code: string;
  title: string;
}

interface CabinetTypeListTabProps {
  kind: CabinetListKind;
  /** Пояснение, когда в базе ещё нет ни одного типа. */
  emptyText: string;
}

/**
 * Общий список задач/заявок для вкладки кабинета: тип → существующий
 * AutoListForm → существующая AutoObjectForm. Тип загружается с сервера
 * (ListTaskTypes/ListOrderTypes) — клиент не хардкодит коды типов.
 */
export function CabinetTypeListTab({ kind, emptyText }: CabinetTypeListTabProps) {
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [typeCode, setTypeCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [record, setRecord] = useState<{ id?: string } | null>(null);

  const loadTypes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sdk = getSDK();
      const response: any = kind === 'task'
        ? await sdk.task.listTaskTypes({})
        : await sdk.order.listOrderTypes({});
      const items: TypeOption[] = ((response as any)?.items || []).map((t: any) => ({
        code: String(t.code || ''),
        title: String(t.synonym || t.name || t.code || ''),
      })).filter((t: TypeOption) => t.code !== '');

      setTypes(items);
      setTypeCode((current) => (
        items.some((t) => t.code === current) ? current : (items[0]?.code || '')
      ));
    } catch (e) {
      log.error(`load ${kind} types failed`, { error: String(e) });
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    void loadTypes();
  }, [loadTypes]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <div className="text-sm text-destructive break-words max-w-md">{error}</div>
        <Button size="sm" variant="outline" onClick={() => void loadTypes()}>
          <RefreshCw className="mr-1 h-3 w-3" />
          Повторить
        </Button>
      </div>
    );
  }

  if (!types.length || !typeCode) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md text-center text-sm text-muted-foreground">{emptyText}</div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Типов несколько — переключатель (один тип не занимает место) */}
      {types.length > 1 && (
        <div className="shrink-0 border-b bg-background/60 px-3 py-1.5">
          <TabBar
            tabs={types.map((t) => ({ id: t.code, label: t.title }))}
            active={typeCode}
            onChange={setTypeCode}
            group={`cabinet-${kind}-types`}
          />
        </div>
      )}

      <div className="min-h-0 flex-1">
        <AutoListForm
          key={`${kind}:${typeCode}`}
          objectType={kind}
          objectName={typeCode}
          onRowDoubleClick={(row) => setRecord({ id: String(row.id || '') })}
          onCreate={() => setRecord({})}
          foldersPanel
        />
      </div>

      {/* Форма записи — существующая AutoObjectForm (модальное окно платформы). */}
      {record && (
        <AutoObjectForm
          objectType={kind}
          objectName={typeCode}
          objectId={record.id}
          onClose={() => setRecord(null)}
        />
      )}
    </div>
  );
}
